import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Lock, Loader2 } from 'lucide-react';

export function PinModal({ open, onClose, onSubmit, loading, title, description }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setDigits(['', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [open]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && digits.every(d => d)) {
      onSubmit(digits.join(''));
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (paste.length === 4) {
      setDigits(paste.split(''));
      inputRefs.current[3]?.focus();
    }
  };

  const pin = digits.join('');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-xs" data-testid="pin-modal">
        <DialogHeader className="text-center">
          <DialogTitle className="flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            {title || 'Enter Transaction PIN'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {description || 'Enter your 4-digit PIN to authorize this transaction'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-3 py-4" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={`digit-${i}`}
              ref={el => { inputRefs.current[i] = el; }}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl bg-muted/30 focus:border-primary focus:outline-none transition-colors"
              data-testid={`pin-digit-${i}`}
            />
          ))}
        </div>
        <Button
          onClick={() => onSubmit(pin)}
          disabled={pin.length < 4 || loading}
          className="w-full"
          data-testid="pin-submit-btn"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying...</> : 'Confirm'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
